import { useEffect, useMemo, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import LinkModal from "./LinkModal";

const buttonBase = {
  border: "1px solid #dbe3ef",
  borderRadius: 6,
  background: "#fff",
  color: "#334155",
  minWidth: 32,
  height: 30,
  padding: "0 8px",
  fontSize: 12,
  fontWeight: 650,
  cursor: "pointer",
};

function ToolbarButton({ label, title, active, onClick }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      style={{ ...buttonBase, ...(active ? { background: "#dbeafe", borderColor: "#93c5fd", color: "#1d4ed8" } : {}) }}
    >
      {label}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Write content...", minHeight = 160 }) {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const extensions = useMemo(() => [
    StarterKit,
    Underline,
    Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
  ], []);

  const editor = useEditor({
    extensions,
    content: value || "",
    editorProps: { attributes: { "aria-label": placeholder, "data-placeholder": placeholder } },
    onUpdate: ({ editor: nextEditor }) => onChange(nextEditor.isEmpty ? "" : nextEditor.getHTML()),
  });

  useEffect(() => {
    if (editor && !editor.isFocused && (value || "") !== (editor.isEmpty ? "" : editor.getHTML())) {
      editor.commands.setContent(value || "", false);
    }
  }, [editor, value]);

  if (!editor) return null;

  const currentLink = editor.getAttributes("link").href || "";

  return (
    <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: 8, borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <ToolbarButton label="B" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton label="I" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton label="U" title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarButton label="S" title="Strike through" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} />
        <ToolbarButton label="P" title="Paragraph" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} />
        <ToolbarButton label="H1" title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} />
        <ToolbarButton label="H2" title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton label="H3" title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <ToolbarButton label="• List" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton label="1. List" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolbarButton label="Quote" title="Block quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <ToolbarButton label="Code" title="Code block" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
        <ToolbarButton label="Table" title="Insert table" active={editor.isActive("table")} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />
        <ToolbarButton label="+ Row" title="Add table row" onClick={() => editor.chain().focus().addRowAfter().run()} />
        <ToolbarButton label="- Row" title="Delete table row" onClick={() => editor.chain().focus().deleteRow().run()} />
        <ToolbarButton label="+ Col" title="Add table column" onClick={() => editor.chain().focus().addColumnAfter().run()} />
        <ToolbarButton label="- Col" title="Delete table column" onClick={() => editor.chain().focus().deleteColumn().run()} />
        <ToolbarButton label="Del Table" title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()} />
        <ToolbarButton label="Link" title="Add or edit link" active={editor.isActive("link")} onClick={() => setLinkModalOpen(true)} />
        <ToolbarButton label="↶" title="Undo" onClick={() => editor.chain().focus().undo().run()} />
        <ToolbarButton label="↷" title="Redo" onClick={() => editor.chain().focus().redo().run()} />
      </div>
      <EditorContent
        editor={editor}
        style={{ minHeight, padding: "10px 12px", lineHeight: 1.6, color: "#1e293b" }}
      />
      <style>{`
      .ProseMirror table { width: 100%; border-collapse: collapse; margin: 1rem 0; } .ProseMirror th, .ProseMirror td { border: 1px solid #cbd5e1; padding: 8px; min-width: 80px; vertical-align: top; } .ProseMirror th { background: #f1f5f9; font-weight: 600; }
      .ProseMirror h1 { font-size: 1.875rem; font-weight: 700; margin: 1rem 0 .5rem; }
      .ProseMirror h2 { font-size: 1.5rem; font-weight: 600; margin: 1rem 0 .5rem; }
      .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; margin: .75rem 0 .5rem; }
      .ProseMirror ul { list-style: disc; padding-left: 1.5rem; }
      .ProseMirror ol { list-style: decimal; padding-left: 1.5rem; }
      .ProseMirror blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; color: #475569; }
      .ProseMirror pre { background: #0f172a; color: #e2e8f0; padding: 12px; border-radius: 6px; overflow-x: auto; }
      .ProseMirror a { color: #2563eb; text-decoration: underline; }
      .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #94a3b8; float: left; height: 0; pointer-events: none; }
      `}

      </style>
      <style>{`.ProseMirror { min-height: ${Math.max(minHeight - 20, 70)}px; outline: none; } .ProseMirror p:first-child { margin-top: 0; } .ProseMirror p:last-child { margin-bottom: 0; } .ProseMirror[data-placeholder]:empty::before { content: attr(data-placeholder); color: #94a3b8; pointer-events: none; float: left; height: 0; }`}</style>
      {linkModalOpen && <LinkModal initialUrl={currentLink} onClose={() => setLinkModalOpen(false)} onSave={(href) => { editor.chain().focus().extendMarkRange("link").setLink({ href }).run(); setLinkModalOpen(false); }} onRemove={() => { editor.chain().focus().unsetLink().run(); setLinkModalOpen(false); }} />}
    </div>
  );
}
